import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import { useApp } from '../../context';
import { toCsv } from '../../utils/csv.js';
import Avatar from '../shared/Avatar';

function isoWeekMonday(offsetWeeks = 0) {
  const now = new Date();
  now.setUTCDate(now.getUTCDate() + offsetWeeks * 7);
  const dow = now.getUTCDay();
  const daysBack = dow === 0 ? 6 : dow - 1;
  const mon = new Date(now);
  mon.setUTCDate(now.getUTCDate() - daysBack);
  return mon.toISOString().slice(0, 10);
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function fmtDate(dateStr) {
  return new Date(dateStr + 'T00:00:00Z').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
}

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// Saturday and Sunday are shaded so the working week still reads at a glance.
const isWeekend = (i) => i >= 5;

function fmtH(val) {
  const n = parseFloat(val) || 0;
  return n === 0 ? '—' : (n % 1 === 0 ? `${n}h` : `${n.toFixed(1)}h`);
}

// This week's expected hours: the person's own capacity (contracted hours on
// their working days, less bank holidays, closures and approved leave), from
// the server's capacity calendar. The contracted week is the fallback.
const expectedOf = (r) => {
  const e = parseFloat(r.expected_hours);
  if (Number.isFinite(e)) return e;
  return parseFloat(r.expected_weekly_hours) || 37.5;
};

function statusColor(actual, expected) {
  const pct = expected > 0 ? actual / expected : 1;
  if (pct >= 1) return 'text-emerald-700 bg-emerald-50';
  if (pct >= 0.5) return 'text-amber-700 bg-amber-50';
  return 'text-red-700 bg-red-50';
}

export default function TimeOverviewPage() {
  const { currentUser } = useApp();
  const [weekStart, setWeekStart] = useState(isoWeekMonday(0));
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);

  const load = async (ws) => {
    setLoading(true);
    try {
      const res = await api.get('time-overview', { week: ws });
      setData(res);
    } catch {
      setData(null);
    }
    setLoading(false);
  };

  useEffect(() => { load(weekStart); }, [weekStart]);

  const days = data?.days || [];
  const rows = data?.rows || [];

  const prevWeek = () => setWeekStart(addDays(weekStart, -7));
  const nextWeek = () => setWeekStart(addDays(weekStart, 7));

  // Export the displayed week's per-person, per-day breakdown as CSV (client-side).
  const exportCsv = () => {
    if (!rows.length) return;
    const num = (v) => parseFloat(v) || 0;
    const dayHdrs = days.map((d, i) => `${DAY_LABELS[i]} ${d}`);
    const header = ['Person', 'Team / Role', 'Expected (h)', ...dayHdrs, 'Total (h)', 'Overtime (h)'];
    const dataRows = rows.map(r => [
      `${r.first_name || ''} ${r.last_name || ''}`.trim(),
      r.team_name || r.designation || '',
      expectedOf(r),
      ...DAY_KEYS.map(d => num(r[d])),
      num(r.total),
      num(r.overtime_total),
    ]);
    const csv = toCsv(header, dataRows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hours-overview-${weekStart}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const isCurrentWeek = weekStart === isoWeekMonday(0);
  const weekLabel = days.length === 7
    ? `${fmtDate(days[0])} — ${fmtDate(days[6])}`
    : weekStart;

  return (
    <div className="fade-in p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-base font-extrabold text-navy">Hours Overview</h1>
          <div className="text-[9px] text-gray-500 mt-0.5">Weekly time log summary — expected vs. logged hours per person</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prevWeek} className="px-2 py-1 text-[9px] border border-gray-200 rounded bg-white hover:bg-gray-50">← Prev</button>
          <span className="text-[10px] font-semibold text-gray-700 whitespace-nowrap">{weekLabel}</span>
          <button onClick={nextWeek} disabled={isCurrentWeek} className="px-2 py-1 text-[9px] border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-40">Next →</button>
          {!isCurrentWeek && (
            <button onClick={() => setWeekStart(isoWeekMonday(0))} className="px-2 py-1 text-[9px] bg-navy text-white rounded hover:bg-navy/80">This week</button>
          )}
          <button onClick={exportCsv} disabled={loading || rows.length === 0}
            className="px-2 py-1 text-[9px] border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-40 font-semibold">
            ⬇ Export CSV
          </button>
        </div>
      </div>

      {/* Summary pills */}
      {!loading && rows.length > 0 && (() => {
        const total = rows.length;
        const onTarget = rows.filter(r => parseFloat(r.total) >= expectedOf(r)).length;
        const under = rows.filter(r => parseFloat(r.total) > 0 && parseFloat(r.total) < expectedOf(r)).length;
        const none = rows.filter(r => parseFloat(r.total) === 0).length;
        const otHours = rows.reduce((s, r) => s + (parseFloat(r.overtime_total) || 0), 0);
        const otPending = rows.reduce((s, r) => s + (parseFloat(r.overtime_pending) || 0), 0);
        return (
          <div className="flex gap-2 mb-4 flex-wrap">
            <div className="text-[9px] px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{onTarget} on target</div>
            <div className="text-[9px] px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold">{under} under target</div>
            <div className="text-[9px] px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-semibold">{none} no hours logged</div>
            {otHours > 0 && (
              <div className="text-[9px] px-2.5 py-1 rounded-full bg-orange-100 text-orange-700 font-semibold">
                {fmtH(otHours)} overtime{otPending > 0 ? ` · ${fmtH(otPending)} pending approval` : ''}
              </div>
            )}
          </div>
        );
      })()}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        {loading ? (
          <div className="text-center py-12 text-[10px] text-gray-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 text-[10px] text-gray-400">No data for this period.</div>
        ) : (
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 font-bold">Person</th>
                <th className="px-3 py-2 text-center text-[8px] uppercase text-gray-500 font-bold">Expected</th>
                {days.map((d, i) => (
                  <th key={d} className={`px-2 py-2 text-center text-[8px] uppercase text-gray-500 font-bold ${isWeekend(i) ? 'bg-gray-100' : ''}`}>
                    {DAY_LABELS[i]}<br />
                    <span className="text-[7px] font-normal">{new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })}</span>
                  </th>
                ))}
                <th className="px-3 py-2 text-center text-[8px] uppercase text-gray-500 font-bold">Total</th>
                <th className="px-3 py-2 text-center text-[8px] uppercase text-gray-500 font-bold">Overtime</th>
                <th className="px-3 py-2 text-center text-[8px] uppercase text-gray-500 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const actual = parseFloat(r.total) || 0;
                const expected = expectedOf(r);
                const pct = expected > 0 ? Math.min(100, Math.round(actual / expected * 100)) : 100;
                const sc = statusColor(actual, expected);
                return (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Avatar personId={r.id} className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[8px] font-bold shrink-0"
                          style={{ background: r.avatar_color || '#1B2A4A' }}>
                          {(r.first_name?.[0] || '').toUpperCase()}{(r.last_name?.[0] || '').toUpperCase()}
                        </Avatar>
                        <div>
                          <div className="text-[10px] font-semibold text-navy">{r.first_name} {r.last_name}</div>
                          {(r.team_name || r.designation) && (
                            <div className="text-[8px] text-gray-400">{r.team_name || r.designation}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center text-[10px] text-gray-500"
                        title={r.expected_note ? `Contracted ${r.contracted_hours ?? r.expected_weekly_hours}h, less ${r.expected_note}` : undefined}>
                      {expected}h
                      {r.expected_note && <div className="text-[7.5px] text-amber-600 leading-tight">less {r.expected_note}</div>}
                    </td>
                    {DAY_KEYS.map((day, i) => (
                      <td key={day} className={`px-2 py-2 text-center text-[10px] ${parseFloat(r[day]) > 0 ? 'text-gray-800 font-medium' : 'text-gray-300'} ${isWeekend(i) ? 'bg-gray-50' : ''}`}>
                        {fmtH(r[day])}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] font-bold ${actual > 0 ? 'text-gray-800' : 'text-gray-300'}`}>
                        {fmtH(actual)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {parseFloat(r.overtime_total) > 0 ? (
                        <div className="inline-flex flex-col items-center">
                          <span className="text-[10px] font-bold text-orange-600">{fmtH(r.overtime_total)}</span>
                          {parseFloat(r.overtime_pending) > 0 && (
                            <span className="text-[7px] font-semibold text-orange-400">{fmtH(r.overtime_pending)} pending</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-[10px] text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-bold ${sc}`}>
                        {pct}%
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-3 text-[8px] text-gray-400 bg-blue-50 border border-blue-100 rounded px-3 py-2">
        Expected hours are each person's contracted week from their <strong>People</strong> profile (weekly hours and working days), less bank holidays, closures and approved leave that week.
        Hours logged on a Saturday or Sunday are shown in the shaded columns and count towards the week's total.
        Staff log all the hours they work here; <strong>overtime, standby and call-outs are claimed on My Overtime</strong>.
        A daily weekday reminder nudges anyone with no time logged for the previous working day — toggle it in <strong>Admin → Settings → Integrations</strong>.
      </div>
    </div>
  );
}
